import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useUploadSeedFile} from '../../../hooks/productions/useProductions';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import '../Demo.scss';

const UploadSeedFileApiTest: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const uploadSeedFileMutation = useUploadSeedFile(
    (data) => {
      console.log('Seed file uploaded successfully:', data);
    },
    (error) => {
      console.error('Error uploading seed file:', error);
    }
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadSeedFileMutation.mutate(selectedFile);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    // Reset file input
    const fileInput = document.getElementById(
      'seed-file-input'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleCopyJson = async () => {
    if (uploadSeedFileMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(uploadSeedFileMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🌱 Upload Seed File API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Upload a seed file for production processing
      </Typography>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" data-testid={`upload-seed-file-api-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Upload Seed File
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/upload/seed-file
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Upload a seed file for production processing. Returns the uploaded
            file path and a success message.
          </Typography>
        </CardContent>
      </Card>

      {/* File Upload Section */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            File Upload
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <input
              id="seed-file-input"
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              onChange={handleFileChange}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                fontSize: '14px',
              }}
            />
          </Box>

          {selectedFile && (
            <Box sx={{marginBottom: 2}}>
              <Typography
                className="typo-text body3_size"
                sx={{marginBottom: 1}}
              >
                <strong>Selected File:</strong>
              </Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Chip
                  label={selectedFile.type || 'Unknown type'}
                  color="primary"
                  size="small"
                  data-testid={`upload-seed-file-api-chip-select`}
                />
                <Typography className="typo-text body3_size">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}{' '}
                  KB)
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploadSeedFileMutation.isPending}
            >
              {uploadSeedFileMutation.isPending
                ? '⏳ Uploading...'
                : '🚀 Upload Seed File'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
          </Box>

          {uploadSeedFileMutation.isPending && (
            <Box sx={{marginBottom: 2}}>
              <Typography
                className="typo-text body3_size"
                sx={{marginBottom: 1}}
              >
                Uploading seed file...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {uploadSeedFileMutation.error && (
            <Alert severity="error" sx={{marginTop: 2}}>
              <Typography className="typo-text subhead3_size" color="error">
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size" color="error">
                {uploadSeedFileMutation.error.message}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Success State - Display Response */}
      {uploadSeedFileMutation.data && !uploadSeedFileMutation.isPending && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Success Message */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              🎉 Seed File Uploaded Successfully!
            </Typography>
            <Typography className="typo-text body3_size" gutterBottom>
              <strong>Message:</strong> {uploadSeedFileMutation.data.message}
            </Typography>
            <Typography className="typo-text body3_size">
              <strong>Seed File Path:</strong>{' '}
              {uploadSeedFileMutation.data.seedFilePath}
            </Typography>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Response Data */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📥 Response Data
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Response'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(uploadSeedFileMutation.data, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Full Response */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 Full JSON Response
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: '#263238',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '500px',
                  }}
                >
                  <pre
                    style={{
                      color: '#4caf50',
                      fontSize: '12px',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(uploadSeedFileMutation.data, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}

      {/* Initial State */}
      {!uploadSeedFileMutation.isPending &&
        !uploadSeedFileMutation.data &&
        !uploadSeedFileMutation.error && (
          <Box
            sx={{
              padding: 3,
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ff9800',
            }}
          >
            <Typography className="typo-text body2_size">
              👆 Select a file and click "Upload Seed File" button to make the
              API request
            </Typography>
          </Box>
        )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/upload/seed-file
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> POST
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Content-Type:</strong> multipart/form-data
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Request Body:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`FormData with 'file' field containing the selected seed file`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Expected Response:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "message": "string",
  "seedFilePath": "string"
}`}
        </pre>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useUploadSeedFile } from '../../../hooks/productions/useProductions';

const MyComponent = () => {
  const uploadSeedFile = useUploadSeedFile(
    (data) => console.log('Success:', data),
    (error) => console.error('Error:', error)
  );

  const handleUpload = (file: File) => {
    uploadSeedFile.mutate(file);
  };

  return (
    <input
      type="file"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) {
          handleUpload(file);
        }
      }}
    />
  );
};`}
        </pre>
      </Box>
    </Box>
  );
};

export default UploadSeedFileApiTest;
